import React from "react";
import {
  Zap, Package, FlaskConical, Star, MessageCircle, Newspaper,
  ArrowRightLeft, Scale, Smartphone, BadgePercent, Camera, Megaphone,
  Tag, Folder, Heart, Flame, Sparkles, Gift, Crown, Rocket,
  Target, Layers, Bookmark, ThumbsUp,
} from "lucide-react";
import type { BucketId } from "./static-ad-buckets";

export interface BucketVisual {
  icon: React.ReactNode;
  accent: string;
  bar: string;
}

// Single source of truth for the icon + color treatment of every Ads
// Express category. Shared across the Ads Express picker, the Static Ad
// Units library, and the Kill Crew variant so the categories always look
// the same everywhere. Must cover every BucketId.
export const BUCKET_VISUALS: Record<BucketId, BucketVisual> = {
  "hooks":            { icon: <Zap className="h-4 w-4" />,            accent: "bg-orange-500/15 text-orange-600 dark:text-orange-400",    bar: "bg-orange-500" },
  "product-benefits": { icon: <Package className="h-4 w-4" />,        accent: "bg-blue-500/15 text-blue-600 dark:text-blue-400",          bar: "bg-blue-500" },
  "education":        { icon: <FlaskConical className="h-4 w-4" />,   accent: "bg-teal-500/15 text-teal-600 dark:text-teal-400",          bar: "bg-teal-500" },
  "reviews":          { icon: <Star className="h-4 w-4" />,           accent: "bg-amber-500/15 text-amber-600 dark:text-amber-400",       bar: "bg-amber-500" },
  "social-ugc":       { icon: <MessageCircle className="h-4 w-4" />,  accent: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-500" },
  "press-authority":  { icon: <Newspaper className="h-4 w-4" />,      accent: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400",    bar: "bg-indigo-500" },
  "transformation":   { icon: <ArrowRightLeft className="h-4 w-4" />, accent: "bg-pink-500/15 text-pink-600 dark:text-pink-400",          bar: "bg-pink-500" },
  "comparison":       { icon: <Scale className="h-4 w-4" />,          accent: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400",          bar: "bg-cyan-500" },
  "native-memes":     { icon: <Smartphone className="h-4 w-4" />,     accent: "bg-violet-500/15 text-violet-600 dark:text-violet-400",    bar: "bg-violet-500" },
  "offers-promos":    { icon: <BadgePercent className="h-4 w-4" />,   accent: "bg-rose-500/15 text-rose-600 dark:text-rose-400",          bar: "bg-rose-500" },
  "lifestyle-brand":  { icon: <Camera className="h-4 w-4" />,         accent: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400", bar: "bg-fuchsia-500" },
  "other":            { icon: <Megaphone className="h-4 w-4" />,      accent: "bg-slate-500/15 text-slate-600 dark:text-slate-400",       bar: "bg-slate-500" },
};

// Admin-managed custom template categories store a color + icon *key* (not raw
// CSS) so the picker headings stay consistent with the legacy buckets and avoid
// arbitrary class injection. The options below are the only allowed values.

export interface CategoryColorOption {
  key: string;
  label: string;
  accent: string;
  bar: string;
}

export const CATEGORY_COLOR_OPTIONS: CategoryColorOption[] = [
  { key: "violet",  label: "Violet",  accent: "bg-violet-500/15 text-violet-600 dark:text-violet-400",   bar: "bg-violet-500" },
  { key: "blue",    label: "Blue",    accent: "bg-blue-500/15 text-blue-600 dark:text-blue-400",         bar: "bg-blue-500" },
  { key: "cyan",    label: "Cyan",    accent: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400",         bar: "bg-cyan-500" },
  { key: "teal",    label: "Teal",    accent: "bg-teal-500/15 text-teal-600 dark:text-teal-400",         bar: "bg-teal-500" },
  { key: "emerald", label: "Emerald", accent: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-500" },
  { key: "amber",   label: "Amber",   accent: "bg-amber-500/15 text-amber-600 dark:text-amber-400",      bar: "bg-amber-500" },
  { key: "orange",  label: "Orange",  accent: "bg-orange-500/15 text-orange-600 dark:text-orange-400",   bar: "bg-orange-500" },
  { key: "rose",    label: "Rose",    accent: "bg-rose-500/15 text-rose-600 dark:text-rose-400",         bar: "bg-rose-500" },
  { key: "pink",    label: "Pink",    accent: "bg-pink-500/15 text-pink-600 dark:text-pink-400",         bar: "bg-pink-500" },
  { key: "fuchsia", label: "Fuchsia", accent: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400", bar: "bg-fuchsia-500" },
  { key: "indigo",  label: "Indigo",  accent: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400",   bar: "bg-indigo-500" },
  { key: "slate",   label: "Slate",   accent: "bg-slate-500/15 text-slate-600 dark:text-slate-400",      bar: "bg-slate-500" },
];

export interface CategoryIconOption {
  key: string;
  label: string;
  icon: React.ReactNode;
}

export const CATEGORY_ICON_OPTIONS: CategoryIconOption[] = [
  { key: "tag",       label: "Tag",       icon: <Tag className="h-4 w-4" /> },
  { key: "folder",    label: "Folder",    icon: <Folder className="h-4 w-4" /> },
  { key: "star",      label: "Star",      icon: <Star className="h-4 w-4" /> },
  { key: "heart",     label: "Heart",     icon: <Heart className="h-4 w-4" /> },
  { key: "flame",     label: "Flame",     icon: <Flame className="h-4 w-4" /> },
  { key: "sparkles",  label: "Sparkles",  icon: <Sparkles className="h-4 w-4" /> },
  { key: "zap",       label: "Bolt",      icon: <Zap className="h-4 w-4" /> },
  { key: "gift",      label: "Gift",      icon: <Gift className="h-4 w-4" /> },
  { key: "crown",     label: "Crown",     icon: <Crown className="h-4 w-4" /> },
  { key: "rocket",    label: "Rocket",    icon: <Rocket className="h-4 w-4" /> },
  { key: "target",    label: "Target",    icon: <Target className="h-4 w-4" /> },
  { key: "layers",    label: "Layers",    icon: <Layers className="h-4 w-4" /> },
  { key: "package",   label: "Package",   icon: <Package className="h-4 w-4" /> },
  { key: "megaphone", label: "Megaphone", icon: <Megaphone className="h-4 w-4" /> },
  { key: "bookmark",  label: "Bookmark",  icon: <Bookmark className="h-4 w-4" /> },
  { key: "thumbsup",  label: "Thumbs Up", icon: <ThumbsUp className="h-4 w-4" /> },
];

const DEFAULT_CATEGORY_COLOR = CATEGORY_COLOR_OPTIONS[0];
const DEFAULT_CATEGORY_ICON_KEY = "folder";

// Resolve a stored color/icon key pair into the same BucketVisual shape used by
// the legacy buckets. Falls back gracefully when a key is missing/unknown.
export function getCategoryVisual(
  color?: string | null,
  icon?: string | null,
): BucketVisual {
  const colorOpt = CATEGORY_COLOR_OPTIONS.find((c) => c.key === color) ?? DEFAULT_CATEGORY_COLOR;
  const iconOpt =
    CATEGORY_ICON_OPTIONS.find((i) => i.key === (icon ?? DEFAULT_CATEGORY_ICON_KEY)) ??
    CATEGORY_ICON_OPTIONS.find((i) => i.key === DEFAULT_CATEGORY_ICON_KEY)!;
  return { icon: iconOpt.icon, accent: colorOpt.accent, bar: colorOpt.bar };
}
